import { useCallback, useEffect, useMemo, useState } from 'react';

import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { TReactSelectOption } from '@ui/select/react-select/custom-react-select.types';
import { selectBtvChannelCategories } from '@/store/slices/btv-channel-categories/selectors';
import { selectProjectBtvChannelCategories } from '@/store/slices/project/selectors';
import { saveWorkingProjectByFields } from '@/store/slices/project';
import useLoadBtvChannelCategories from '@/connection/hooks/use-load-btv-channel-categories';
import EditStateSelect from '@ui/edit-state-wrapper/edit-state-select';
import preventMultiValueArrowNav from '@ui/edit-state-wrapper/prevent-multi-value-arrow-nav';
import Container from '@ui/container/container';
import { GENERAL_EDITABLE_PERMISSIONS } from '@/types/permissions.types';
import useHasPermission from '@/utils/authentication/use-has-permission';

import styles from '../edit-project-category.module.css';

type TCategoryOption = TReactSelectOption<string, number, number>;

const BtvChannelCategoriesSelect = () => {
  useLoadBtvChannelCategories();

  const dispatch = useAppDispatch();
  const { hasPermission: hasPermissions } = useHasPermission({
    allowedRoles: GENERAL_EDITABLE_PERMISSIONS,
  });

  const projectBtvChannelCategories = useAppSelector(selectProjectBtvChannelCategories);
  const btvChannelCategories = useAppSelector(selectBtvChannelCategories);

  const initialCategories = useMemo((): TCategoryOption[] => {
    if (!projectBtvChannelCategories || !btvChannelCategories) return [];
    return projectBtvChannelCategories
      .map((id) => {
        const match = btvChannelCategories.find((c) => c.id === id);
        if (!match) return null;
        return { label: match.title, value: match.id, meta: match.id };
      })
      .filter(Boolean) as TCategoryOption[];
  }, [btvChannelCategories, projectBtvChannelCategories]);

  const [displayCategories, setDisplayCategories] = useState<TReactSelectOption[]>(initialCategories);

  useEffect(() => {
    setDisplayCategories(initialCategories);
  }, [initialCategories]);

  const options = useMemo(() => {
    if (!btvChannelCategories) return [];
    return btvChannelCategories
      .filter((c) => !c.isDefault)
      .map((c) => ({
        label: c.title,
        value: c.id,
        meta: c,
      }));
  }, [btvChannelCategories]);

  const handleMenuClose = useCallback(
    (selectedOptions: TReactSelectOption[]) => {
      const updatedCategories = selectedOptions.map((option) => ({
        label: option.label.toString(),
        value: option.value,
        meta: option.meta || option.value,
      })) as TCategoryOption[];

      setDisplayCategories(updatedCategories);

      if (initialCategories.length === updatedCategories.length) {
        const defaultSet = new Set(initialCategories.map((o) => o.value));
        const updatedSet = new Set(updatedCategories.map((o) => o.value));
        if (Array.from(defaultSet).every((v) => updatedSet.has(v))) return;
      }

      dispatch(
        saveWorkingProjectByFields({
          btv_channel_categories: updatedCategories.map((o) => o.value),
        })
      );
    },
    [dispatch, initialCategories]
  );

  return (
    <Container className={`z-1 ${styles.wrapper}`}>
      <EditStateSelect
        Label="CATEGORIES"
        placeholder="Add category"
        value={displayCategories}
        options={options}
        onChange={(selected) => setDisplayCategories(selected)}
        onMenuClose={() => handleMenuClose(displayCategories)}
        disabled={!hasPermissions}
        onKeyDown={preventMultiValueArrowNav}
        noOptionsMessage="No Categories Exist"
      />
    </Container>
  );
};

export default BtvChannelCategoriesSelect;
